import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import AppHeader from '../components/AppHeader';
import CollabMeta from '../components/CollabMeta';
import Lightbox from '../components/Lightbox';
import { LinksField, TagsField } from '../components/ListFields';
import { useAuth } from '../lib/auth';
import {
  type CollabEdits,
  CollabConflict,
  deleteCollabNote,
  editorName,
  saveCollabNote,
  useCollabNote,
} from '../lib/collab';
import type { CollaborativeNote } from '../lib/types';
import { useDateFormatter } from '../lib/useIdeas';

function messageFor(e: unknown): string {
  const code = (e as { code?: string }).code ?? '';
  return `${(e as Error).message}${code ? ` (${code})` : ''}`;
}

/** The form's starting point: the note as it stood when Edit was pressed. */
function editsFrom(note: CollaborativeNote): CollabEdits {
  return {
    title: note.title ?? '',
    description: note.description ?? '',
    tags: note.tags ?? [],
    links: note.links ?? [],
  };
}

function sameEdits(a: CollabEdits, b: CollabEdits): boolean {
  // NUL join: a separator no tag or URL can contain.
  return (
    a.title === b.title &&
    a.description === b.description &&
    a.tags.join('\0') === b.tags.join('\0') &&
    a.links.join('\0') === b.links.join('\0')
  );
}

/**
 * `/collab/:id` — one collaborative note, live, editable by any member.
 *
 * The view is a live subscription, so an edit made on an iPhone appears as it lands. Editing
 * freezes a copy: the form is seeded once from the note as it stood, and `opened` remembers
 * that version's `updatedAt` so the save can tell whether anyone else got there first. See
 * `saveCollabNote` for why the save touches content fields only.
 *
 * Images are shown, not edited. No client can add one: uploads under `ideas/{id}/images/`
 * need the caller to own that idea, and the idea was deleted when the note became shared.
 */
export default function CollabNote() {
  const { id } = useParams();
  const { user, profile } = useAuth();
  const { note, error, loading, pending } = useCollabNote(id);
  const [viewing, setViewing] = useState<number | null>(null);
  const fmt = useDateFormatter();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<CollabEdits | null>(null);
  const [base, setBase] = useState<CollabEdits | null>(null);
  const [opened, setOpened] = useState<Timestamp | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const images = note?.images ?? [];
  const editing = draft !== null;
  const dirty = !!draft && !!base && !sameEdits(draft, base);

  // Someone else saved while this editor was open. Shown before Save is pressed, so the
  // conflict is news on arrival rather than a surprise at the end.
  const changedUnderneath =
    editing &&
    !saving &&
    !!note?.updatedAt &&
    !!opened &&
    note.updatedAt.toMillis() !== opened.toMillis();

  function startEditing() {
    if (!note) return;
    const seed = editsFrom(note);
    setDraft(seed);
    setBase(seed);
    // A note with no `updatedAt` at all predates the field; epoch makes any real one that
    // appears in the meantime count as a newer save.
    setOpened(note.updatedAt ?? Timestamp.fromMillis(0));
    setSaveError(null);
    setConflict(null);
  }

  function stopEditing() {
    setDraft(null);
    setBase(null);
    setOpened(null);
    setConflict(null);
    setSaveError(null);
  }

  async function save(force: boolean) {
    if (!user || !id || !draft) return;
    setSaving(true);
    setSaveError(null);
    try {
      await saveCollabNote(
        id,
        draft,
        { uid: user.uid, name: editorName(user, profile) },
        force ? null : opened
      );
      stopEditing();
    } catch (e) {
      if (e instanceof CollabConflict) {
        setConflict(e.editorName);
      } else {
        // Visible, like every failure path in this client: a silent catch here would look
        // exactly like a save that went through.
        setSaveError(messageFor(e));
      }
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!id) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteCollabNote(id);
      navigate('/collab');
    } catch (e) {
      setDeleteError(messageFor(e));
      setDeleting(false);
    }
  }

  const update = (patch: Partial<CollabEdits>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  return (
    <>
      <AppHeader
        title={editing ? 'Edit collaborative note' : 'Collaborative note'}
        back={{ to: '/collab', label: 'Collaborative' }}
      />

      <main>
        {loading && <p className="muted">Loading…</p>}
        {error && (
          <p className="error">
            {error} <Link to="/collab">Back to collaborative notes</Link>.
          </p>
        )}
        {!loading && !error && !note && (
          <p className="muted">
            That note no longer exists. <Link to="/collab">Back to collaborative notes</Link>.
          </p>
        )}

        {note && user && !editing && (
          <article className="note-view">
            <div className="note-head">
              <h1>{note.title || 'Untitled'}</h1>
              <button type="button" disabled={pending} onClick={startEditing}>
                {pending ? 'Saving…' : 'Edit'}
              </button>
            </div>

            <div className="meta">
              <CollabMeta note={note} uid={user.uid} />
              {note.updatedAt && (
                <span>
                  Edited {fmt.format(note.updatedAt.toDate())}
                  {note.lastEditedByName &&
                    ` by ${note.lastEditedBy === user.uid ? 'you' : note.lastEditedByName}`}
                </span>
              )}
            </div>

            {note.description && <p className="body">{note.description}</p>}

            {images.length > 0 && (
              <div className="thumbs">
                {images.map((src, i) => (
                  <div className="thumb" key={src}>
                    <button
                      type="button"
                      className="thumb-open"
                      aria-label={`View image ${i + 1} of ${images.length}`}
                      onClick={() => setViewing(i)}
                    >
                      <img src={src} alt="" loading="lazy" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {note.tags && note.tags.length > 0 && (
              <div className="meta">
                {note.tags.map((tag) => (
                  <span className="tag" key={tag}>{tag}</span>
                ))}
              </div>
            )}

            {note.links && note.links.length > 0 && (
              <ul className="links">
                {note.links.map((href) => (
                  <li key={href}>
                    <a href={href} target="_blank" rel="noopener noreferrer">{href}</a>
                  </li>
                ))}
              </ul>
            )}

            {/* Owner only, as the rules have it. A collaborator who wants out has to be
                removed by the owner, which is an iOS flow. */}
            {note.ownerId === user.uid && (
              <div className="delete-zone">
                {confirmingDelete ? (
                  <div className="confirm" role="alert">
                    <p>
                      <strong>Delete this note for everyone?</strong>{' '}
                      {(note.collaboratorIds?.length ?? 0) > 0
                        ? `It disappears for all ${note.collaboratorIds!.length + 1} people on it, on every device.`
                        : 'It disappears on every device.'}{' '}
                      This cannot be undone.
                    </p>
                    <div className="actions">
                      <button type="button" className="danger" disabled={deleting} onClick={() => void remove()}>
                        {deleting ? 'Deleting…' : 'Delete'}
                      </button>
                      <button type="button" disabled={deleting} onClick={() => setConfirmingDelete(false)}>
                        Keep it
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" className="danger" onClick={() => setConfirmingDelete(true)}>
                    Delete note
                  </button>
                )}
                {deleteError && <p className="error">{deleteError}</p>}
              </div>
            )}
          </article>
        )}

        {note && draft && (
          <form
            className="edit"
            onSubmit={(e) => {
              e.preventDefault();
              void save(false);
            }}
          >
            {changedUnderneath && !conflict && (
              <p className="edit-notice">
                {note.lastEditedByName && note.lastEditedBy !== user?.uid
                  ? `${note.lastEditedByName} saved`
                  : 'Someone saved'}{' '}
                changes since you started editing. Saving will ask before replacing them.
              </p>
            )}

            <label className="field">
              <span className="field-label">Title</span>
              <input
                type="text"
                value={draft.title}
                placeholder="Title"
                autoFocus
                onChange={(e) => update({ title: e.target.value })}
              />
            </label>

            <label className="field">
              <span className="field-label">Description</span>
              <textarea
                rows={8}
                value={draft.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </label>

            <TagsField tags={draft.tags} onChange={(tags) => update({ tags })} />
            <LinksField links={draft.links} onChange={(links) => update({ links })} />

            {conflict && (
              <div className="conflict" role="alert">
                <p>
                  <strong>{conflict} saved changes to this note after you started editing.</strong>{' '}
                  Saving yours replaces their title, description, tags and links.
                </p>
                <div className="actions">
                  <button type="button" className="danger" disabled={saving} onClick={() => void save(true)}>
                    {saving ? 'Saving…' : 'Replace with mine'}
                  </button>
                  <button type="button" disabled={saving} onClick={stopEditing}>
                    Discard mine, show theirs
                  </button>
                </div>
              </div>
            )}

            {saveError && <p className="error">{saveError}</p>}

            {!conflict && (
              <div className="actions">
                <button
                  className="primary"
                  type="submit"
                  // iOS refuses an empty title too; a blank one reads as "Untitled" everywhere.
                  disabled={saving || !dirty || !draft.title.trim()}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" disabled={saving} onClick={stopEditing}>
                  Cancel
                </button>
                {!draft.title.trim() && <span className="muted small">A title is required.</span>}
              </div>
            )}
          </form>
        )}
      </main>

      {viewing !== null && (
        <Lightbox images={images} start={viewing} onClose={() => setViewing(null)} />
      )}
    </>
  );
}
